const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000'

export interface Legislation {
  id: number
  country: string | null
  source_name: string | null
  celex_number: string | null
  source_url: string | null
  title: string | null
  document_type: string | null
  document_type_raw: string | null
  creator: string | null
  subject: string | null
  language: string | null
  document_date: string | null
  pub_date: string | null
  oj_reference: string | null
  oj_series: string | null
  description: string | null
  is_active: boolean
}

export interface WorkdayModule {
  module: string
  impact_type: string
  impact_description: string
}

export interface Analysis {
  id: number
  legislation_id: number
  celex_number: string | null
  country: string | null
  source_name: string | null
  original_title: string | null
  one_liner: string
  title: string
  legislation_type: string
  summary: string
  key_provisions: string[]
  is_relevant: boolean
  relevance_confidence: string
  relevance_reasoning: string
  relevant_to: string[]
  workday_affected: boolean
  workday_modules: WorkdayModule[]
  workday_overall_impact: string
  requires_action: boolean
  is_mandatory: boolean
  action_owner: string
  action_description: string
  compliance_deadline: string | null
  deadline_notes: string
  criticality: string
  criticality_reasoning: string
  response_ideas: string[]
  flagged_for_review: boolean
  review_status: string
  reviewed_by: string | null
  deadline_passed: boolean | null
  status: string
  analysed_at: string | null
}

// What GET /api/analysis returns per item: just what the list renders. Keep in
// sync with ANALYSIS_LIST_FIELDS in api/services/database.py. The detail page
// loads the full Analysis with fetchAnalysisDetail.
export type AnalysisSummary = Pick<Analysis,
  | 'id' | 'legislation_id' | 'celex_number' | 'country' | 'title' | 'one_liner'
  | 'key_provisions' | 'is_relevant' | 'relevant_to' | 'workday_modules'
  | 'requires_action' | 'compliance_deadline' | 'deadline_passed' | 'criticality'
  | 'flagged_for_review' | 'review_status' | 'reviewed_by' | 'analysed_at'
>

export async function fetchAnalysis(userEmail?: string, teamName?: string): Promise<AnalysisSummary[]> {
  const params = new URLSearchParams()
  if (teamName) {
    params.set('team_name', teamName)
  } else if (userEmail) {
    params.set('user_email', userEmail)
  }
  const qs = params.toString()
  const res = await fetch(`${API_BASE}/api/analysis${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function fetchAnalysisDetail(id: number): Promise<Analysis> {
  const res = await fetch(`${API_BASE}/api/analysis/${id}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function updateAnalysisReview(id: number, review_status: string, user_email: string, team_name?: string): Promise<Analysis> {
  const body: Record<string, string> = { review_status, user_email }
  if (team_name) body.team_name = team_name
  const res = await fetch(`${API_BASE}/api/analysis/${id}/review`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function fetchHealth(): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE}/api/health`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export interface CountryCount {
  country: string
  count: number
}

export async function fetchCountries(): Promise<CountryCount[]> {
  const res = await fetch(`${API_BASE}/api/countries`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function fetchLegislation(limit = 50, offset = 0, country?: string): Promise<Legislation[]> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (country) params.set('country', country)
  const res = await fetch(`${API_BASE}/api/legislation?${params.toString()}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function fetchLegislationById(id: number): Promise<Legislation> {
  const res = await fetch(`${API_BASE}/api/legislation/${id}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export interface Team {
  team_name: string
  member_count: number
}

export async function fetchTeams(): Promise<Team[]> {
  const res = await fetch(`${API_BASE}/api/teams`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function joinTeam(user_email: string, team_name: string): Promise<{ user_email: string; team_name: string }> {
  const res = await fetch(`${API_BASE}/api/teams/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_email, team_name }),
  })
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

export async function fetchMembership(user_email: string): Promise<{ user_email: string; team_name: string | null }> {
  const res = await fetch(`${API_BASE}/api/teams/membership?user_email=${encodeURIComponent(user_email)}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}
